<template>
  <div class="item-container">
    <div
      class="btn--corners"
      v-bind:class="{
        'fetching-product': isFetchingProduct,
        required: isRequired,
      }"
      @click="isFetchingProduct ? null : showModal()"
      @mouseenter="mouseover"
    >
      <div class="loader-wrapper" v-if="isFetchingProduct">
        <div class="loader">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="loading-text">{{ loadingText }}</div>
        </div>
      </div>

      <span class="plus errors" v-else-if="hasErrors">
        Product not found :(
      </span>
      <span class="plus" v-else>+</span>
      <span class="requirements" :class="{ required: isRequired }">
        {{ requirementsText }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { bus } from "@/main";

export default {
  mounted() {
    bus.$on("fetchingProduct", (layerId) => {
      if (this.layerId === layerId) {
        this.isFetchingProduct = true;
        this.loadingText = "Fetching product...";
        this.generateLoadingText;
      }
    });
  },

  data() {
    return {
      resizable: false,
      adaptive: false,
      draggable: false,
      canBeShown: false,
      hasErrors: false,
      loadingText: "Fetching product...",
    };
  },

  props: ["layerDisplayName", "layerId", "isFetchingProduct", "isRequired"],

  methods: {
    showModal() {
      bus.$emit("openAddItemModal", {
        layerDisplayName: this.layerDisplayName,
        layerId: this.layerId,
        capsuleId: this.currentCapsule.id,
      });
    },

    mouseover() {
      if (this.hasErrors === true) this.hasErrors = false;
    },
  },

  computed: {
    ...mapGetters(["currentCapsule", "addLayerProductErrors"]),

    generateLoadingText() {
      let that = this;

      setTimeout(function () {
        that.loadingText = "Processing data...";

        setTimeout(function () {
          that.loadingText = "Saving...";
        }, that.randomTime);
      }, that.randomTime);
    },

    requirementsText() {
      return this.isRequired ? "Required" : "Optional";
    },

    randomTime() {
      const min = 3000;
      const max = 5500;

      return Math.round(Math.random() * (max - min + 1)) + min;
    },
  },

  watch: {
    addLayerProductErrors: {
      handler(errors) {
        errors.forEach((error, index) => {
          if (error.layerId === this.layerId) {
            this.isFetchingProduct = false;
            this.hasErrors = true;
          }
        });
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_colour-palette.scss";
@import "~@/styles/_breakpoints.scss";

.item-container {
  display: flex;
  flex-direction: column;
  margin-top: 35px;
  overflow: hidden;
}

$bg-color: #fff;
$accent-color: $primary-accent;
$border-width: 2px;
$padding-width: 2px;
$corner-size: 40px;

.btn--corners {
  width: 236px;
  height: 276px;
  position: relative;
  display: table;
  padding: $padding-width;
  font-size: 72px;
  border: $border-width solid $accent-color;

  &.required {
    border-color: $alizarin;
  }

  &:not(.fetching-product) {
    cursor: pointer;
  }

  @include for-tablet-portrait-up {
    width: 206px;
    height: 246px;
  }

  @include for-tablet-landscape-up {
    width: 200px;
    height: 240px;
  }
}

.btn--corners::before,
.btn--corners::after {
  content: "";
  position: absolute;
  background-color: $bg-color;
  z-index: 0;
  transition: 200ms ease-in;
  transform-origin: center center;
}

.btn--corners::before {
  width: calc(100% + #{$border-width} * 2 - #{$corner-size});
  height: calc(100% + #{$border-width} * 2);
  top: -$border-width;
  left: 50%;
  transform: translateX(-50%);
}

.btn--corners::after {
  height: calc(100% + #{$border-width} * 2 - #{$corner-size});
  width: calc(100% + #{$border-width} * 2);
  left: -$border-width;
  top: 50%;
  transform: translateY(-50%);
}

.btn--corners:not(.fetching-product):hover::before {
  width: 0;
}

.btn--corners:not(.fetching-product):hover::after {
  height: 0;
}

.btn--corners {
  .loader-wrapper,
  span.plus,
  span.requirements {
    min-width: 100%;
    min-height: 100%;
    position: absolute;
    color: #979c9c;
    vertical-align: middle;
    text-decoration: none;
    z-index: 1;
    border: none;
  }

  span.plus {
    display: table-cell;
    top: 27%;
  }
  span.errors {
    font-size: 14px;
  }

  span.requirements {
    display: table-cell;
    font-size: 14px;
    font-style: italic;
    top: 82%;
    letter-spacing: 1px;
    transition: all 500ms ease-in-out;
  }

  &:hover {
    span.requirements {
      color: $primary-accent;

      &.required {
        color: $alizarin;
      }
    }
  }
}

.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  margin-left: 5%;
  transform: translate3d(-50%, -50%, 0);
  text-align: center;

  .loading-text {
    font-size: 12px;
    margin-top: 20px;
    margin-left: -10px;
    transition: all 500ms ease-in-out;
  }
}

.dot {
  width: 14px;
  height: 14px;
  background: #3ac;
  border-radius: 100%;
  display: table-cell;
  animation: slide 1s infinite;

  &:nth-child(1) {
    animation-delay: 0.1s;
    background: $primary-accent;
  }

  &:nth-child(2) {
    animation-delay: 0.2s;
    background: #64aacc;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
    background: #96aacc;
  }

  &:nth-child(4) {
    animation-delay: 0.4s;
    background: #c8aacc;
  }

  &:nth-child(5) {
    animation-delay: 0.5s;
    background: #faaacc;
  }
}

@keyframes slide {
  0% {
    transform: scale(1);
  }

  50% {
    opacity: 0.3;
    transform: scale(2);
  }

  100% {
    transform: scale(1);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/capsuleBuilder/layerGroup/AddItem.vue